import { useCallback, useEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { defaultRangeExtractor, useVirtualizer, type Range } from '@tanstack/react-virtual';
import { CanvasToolbar, type FullscreenControl } from './CanvasToolbar';
import type { VisibleGraph } from './graphModel';
import { flattenTree, nextFocus, type Row } from './graphTree';
import { TREE_ROW_HEIGHT, TreeRow } from './TreeRow';
import { cardMentions } from './useGraphLayout';
import type { ViewMode } from './viewUrl';
import { VIRTUAL_MIN } from './VirtualList';
import { withFocused } from './virtualRange';

const OVERSCAN = 8;
const ACTIVATE_KEYS = new Set(['Enter', ' ']);

/** A move of keyboard focus to a node's row, handled once by identity (a search pick). */
export type TreeFocusRequest = { nodeId: string };

type Props = {
  visible: VisibleGraph;
  expandedIds: ReadonlySet<string>;
  selectedId: string | null;
  /** The documents that mention each entity (`mentionCounts`); shown on the entity rows. */
  mentionCounts: ReadonlyMap<string, number>;
  focusRequest: TreeFocusRequest | null;
  /** A click, Enter, or Space on a row: select the node and show its children, as on the canvas. */
  onActivate: (id: string) => void;
  /** Right, `*`, or a closed row's chevron: show the children, selecting nothing. */
  onExpand: (ids: string[]) => void;
  /** Left, or an open row's chevron. */
  onCollapse: (id: string) => void;
  /** A group's "Show 30 more" row. */
  onShowMore: (groupId: string) => void;
  onExportCsv: () => void;
  onMode: (mode: ViewMode) => void;
  fullscreen?: FullscreenControl;
};

/**
 * The row keyboard focus is on (or would return to), by id; its index holds the place when the row goes. `null` id:
 * whichever row comes to sit at `index` (the first member a "Show 30 more" adds).
 */
type Active = { id: string | null; index: number };

/**
 * The list mode: the visible nodes as an ARIA tree in the canvas's frame, with the canvas's toolbar. One row is
 * tabbable (roving tabindex); the arrow keys, Home, End, and `*` move and open as the tree pattern has them (see
 * `nextFocus`). Past VIRTUAL_MIN rows only the rows in view mount, plus the tabbable one, so focus is never dropped.
 */
export function GraphTree({
  visible,
  expandedIds,
  selectedId,
  mentionCounts,
  focusRequest,
  onActivate,
  onExpand,
  onCollapse,
  onShowMore,
  onExportCsv,
  onMode,
  fullscreen,
}: Props) {
  const rows = useMemo(() => flattenTree(visible, expandedIds), [visible, expandedIds]);
  const nodesById = useMemo(() => new Map(visible.nodes.map((n) => [n.id, n])), [visible]);
  const [active, setActive] = useState<Active | null>(null);
  // Bumped to move DOM focus to the tabbable row once it has rendered.
  const [focusTick, setFocusTick] = useState(0);
  const [handledRequest, setHandledRequest] = useState(focusRequest);
  // A request handled in this render sets the tab stop itself; the check below reads the one it replaces.
  const requested = focusRequest !== handledRequest;
  if (requested) {
    setHandledRequest(focusRequest);
    if (focusRequest) {
      setActive({ id: focusRequest.nodeId, index: 0 });
      setFocusTick((tick) => tick + 1);
    }
  }
  const found = active === null || active.id === null ? -1 : rows.findIndex((r) => r.id === active.id);
  // A row that left the tree however it went (a tree key, Back, a refresh, a collapse in the sidebar, Show 30 more)
  // gives the tab stop to the row now at its place. Focus follows when it was in the tree: while rendering, the page
  // still holds the last commit, so the row that goes still has it.
  if (!requested && active !== null && found < 0) {
    const index = Math.min(active.index, rows.length - 1);
    setActive({ id: rows[index].id, index });
    if (document.activeElement?.closest('[role="tree"]')) setFocusTick((tick) => tick + 1);
  }
  // The active row; with none yet, the selection, else the root.
  const activeIndex =
    found >= 0
      ? found
      : active !== null
        ? Math.min(active.index, rows.length - 1)
        : Math.max(selectedId === null ? -1 : rows.findIndex((r) => r.id === selectedId), 0);

  const scrollRef = useRef<HTMLDivElement>(null);
  const windowed = rows.length > VIRTUAL_MIN;
  const rangeExtractor = useCallback(
    (range: Range) => withFocused(defaultRangeExtractor(range), activeIndex),
    [activeIndex],
  );
  // The app does not run the React Compiler, so nothing memoises this component; the warning does not apply.
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: windowed ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => TREE_ROW_HEIGHT,
    overscan: OVERSCAN,
    getItemKey: (index) => rows[index].id,
    rangeExtractor,
  });
  useEffect(() => {
    if (focusTick === 0) return;
    // Focusing the row scrolls it into view; a windowed tree keeps it mounted wherever it is (see `rangeExtractor`).
    scrollRef.current?.querySelector<HTMLElement>('[role="treeitem"][tabindex="0"]')?.focus();
  }, [focusTick]);

  const moveTo = (index: number) => {
    setActive({ id: rows[index].id, index });
    setFocusTick((tick) => tick + 1);
  };
  const activateRow = (row: Row, index: number) => {
    if (row.kind !== 'more') {
      onActivate(row.id);
      return;
    }
    if (row.parentId !== null) onShowMore(row.parentId);
    // The first member the page adds takes the row's place, and the tab stop with it (not the next "Show 30 more").
    setActive({ id: null, index });
  };
  const keyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const row = rows[activeIndex];
    if (!row) return;
    if (ACTIVATE_KEYS.has(event.key)) {
      event.preventDefault();
      activateRow(row, activeIndex);
      return;
    }
    const move = nextFocus(rows, activeIndex, event.key);
    if (move === null) {
      // At the ends, Up and Down would scroll the box instead.
      if (event.key.startsWith('Arrow') || event.key === 'Home' || event.key === 'End') event.preventDefault();
      return;
    }
    event.preventDefault();
    if (move.kind === 'focus') moveTo(move.index);
    else if (move.kind === 'expand') onExpand(move.ids);
    else onCollapse(move.id);
  };
  // A row focused by a click or by Tab becomes the tabbable one, recorded by id, so its removal is noticed above.
  const focusRow = (event: FocusEvent<HTMLDivElement>) => {
    const id = (event.target as HTMLElement).closest<HTMLElement>('[data-row-id]')?.dataset.rowId;
    const index = id === undefined ? -1 : rows.findIndex((r) => r.id === id);
    if (index >= 0 && (active === null || active.id !== rows[index].id)) setActive({ id: rows[index].id, index });
  };

  const renderRow = (index: number, placement?: { start: number }) => {
    const row = rows[index];
    const node = nodesById.get(row.id);
    const toggle =
      row.parentId === null || !row.expandable
        ? undefined
        : () => (row.expanded ? onCollapse(row.id) : onExpand([row.id]));
    return (
      <TreeRow
        key={row.id}
        row={row}
        node={node}
        mentions={node ? cardMentions(node, mentionCounts) : undefined}
        selected={row.id === selectedId}
        tabbable={index === activeIndex}
        onClick={() => activateRow(row, index)}
        onToggle={toggle}
        {...(placement
          ? { className: 'absolute top-0 left-0 w-full', style: { transform: `translateY(${placement.start}px)` } }
          : {})}
      />
    );
  };

  // The canvas's frame, so the page does not move when the mode changes; in full screen it fills the height.
  return (
    <div className="relative flex h-[70vh] min-h-[480px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white lg:flex-1 lg:self-start lg:[:fullscreen_&]:h-auto lg:[:fullscreen_&]:self-stretch dark:border-zinc-700 dark:bg-zinc-900">
      <div className="relative h-12 shrink-0 border-b border-gray-200 dark:border-zinc-700">
        <CanvasToolbar mode="list" onMode={onMode} onExportCsv={onExportCsv} fullscreen={fullscreen} />
      </div>
      <div
        ref={scrollRef}
        role="tree"
        aria-label="Project graph as a tree"
        className="min-h-0 flex-1 overflow-y-auto p-2"
        onKeyDown={keyDown}
        onFocus={focusRow}
      >
        {/* One wrapper in both cases, so a tree that crosses VIRTUAL_MIN keeps its rows mounted (and the focused one
            its focus). Windowed, the height and offsets come from the virtualizer, so they are inline styles. */}
        <div className="relative w-full" style={windowed ? { height: virtualizer.getTotalSize() } : undefined}>
          {windowed
            ? virtualizer.getVirtualItems().map((item) => renderRow(item.index, item))
            : rows.map((_, index) => renderRow(index))}
        </div>
      </div>
    </div>
  );
}
